import React, { useEffect, useState } from "react";
import api, { authFetch } from "@/lib/api";
import PuliziaFascicoliDialog from "@/components/PuliziaFascicoliDialog";
import ImportLibriManutenzione from "@/components/ImportLibriManutenzione";
import ImportDocumentiDialog from "@/components/ImportDocumentiDialog";
import { PROFILO_MEZZI } from "@/lib/profiliImport";
import SpostaCategoria from "@/components/SpostaCategoria";
import CampoUtilizzatore from "@/components/CampoUtilizzatore";
import DoppioniInterventi from "@/components/DoppioniInterventi";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { puoGestire } from "@/lib/ruoli";
import { useDaCerca } from "@/lib/cerca";
import { vedeICosti } from "@/lib/costi";
import {
  Plus, Search, Truck, Wrench, AlertTriangle, CheckCircle2, UploadCloud, ChevronRight, ArrowLeft,
  Folder, X, FileSpreadsheet, Archive,
  Zap, Gauge, Construction, Radio, Trash2, BookOpen, ListChecks
} from "lucide-react";

const EQUIPMENT_ICON_MAP = {
  AUTOCARRI: Truck, AUTOVETTURE: Truck, FURGONI: Truck, "FURGONI ATTREZZATI": Truck,
  ESCAVATORI: Construction, "MACCHINE SPECIALI": Construction,
  GENERATORI: Zap, TAGLIASFALTO: Construction,
  ESPLOSIMETRI: Radio, CERCAFUGHE: Radio,
  PEAD: Wrench, FILIERE: Wrench, RAVETTI: Wrench, PESTELLO: Wrench, BATTIASFALTO: Wrench,
  "STRUMENTI DI COLLAUDO": Gauge,
};
function EquipmentIcon({ category, className }) {
  const Cmp = EQUIPMENT_ICON_MAP[category] || Wrench;
  return <Cmp className={className} />;
}
import { Link as RouterLink } from "react-router-dom";

const STATUS_LABEL = {
  active: "Attiva", maintenance: "In manutenzione",
  dismissed: "Dismessa", stolen: "Rubata", scrapped: "Smaltita", sold: "Venduta"
};
const STATUS_COLOR = {
  active: "bg-emerald-100 text-emerald-700",
  maintenance: "bg-amber-100 text-amber-700",
  dismissed: "bg-slate-200 text-slate-600",
  stolen: "bg-red-100 text-red-700",
  scrapped: "bg-slate-300 text-slate-700",
  sold: "bg-blue-100 text-blue-700"
};
const CADENCE_PRESETS = [
  { label: "Trimestrale (3 mesi)", value: 3 },
  { label: "Quadrimestrale (4 mesi)", value: 4 },
  { label: "Semestrale (6 mesi)", value: 6 },
  { label: "Annuale (12 mesi)", value: 12 },
  { label: "Biennale (24 mesi)", value: 24 },
];

const today = () => new Date().toISOString().substring(0, 10);
const due30 = () => new Date(Date.now() + 30 * 86400000).toISOString().substring(0, 10);

export default function Attrezzature() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Import, spostamenti di categoria e pulizia doppioni sono
  // `require_gestione`: l'amministrazione li fa. Pulizia documenti e
  // «uniforma controlli» restano all'amministratore, perche' toccano tutte
  // le macchine in una volta e non si disfano.
  const gestisce = puoGestire(user);
  // Registrare una macchina nuova la fa anche il tecnico: e' lui che se la
  // trova davanti. Le operazioni di massa - import, pulizie, spostamenti di
  // categoria - restano dell'amministratore.
  const puoAggiungere = ["admin", "amministrativo", "tecnico"].includes(user?.role);

  // L'elenco dei controlli, uguale per tutte le macchine della stessa
  // categoria. Prima si guarda cosa cambierebbe: è una scrittura sui dati
  // veri, e l'elenco che una macchina ha adesso viene sostituito.
  const [uniforma, setUniforma] = useState(null);
  const [applicando, setApplicando] = useState(false);

  const apriUniforma = async () => {
    try {
      const { data } = await api.post(
        "/equipment/controlli-standard/applica?solo_verifica=true");
      setUniforma(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella verifica");
    }
  };

  const applicaUniforma = async () => {
    setApplicando(true);
    try {
      const { data } = await api.post(
        "/equipment/controlli-standard/applica?solo_verifica=false");
      toast.success(`Elenco controlli allineato su ${data.quante_cambiate} macchine`);
      setUniforma(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally { setApplicando(false); }
  };
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [search, setSearch] = useState("");
  const [openCat, setOpenCat] = useState(null);
  // Tornando da una macchina si rientra nella sua categoria, non in cima
  // all'elenco: il percorso in testa alla scheda del mezzo porta qui con
  // ?categoria=...
  useDaCerca("categoria", setOpenCat);
  const [showForm, setShowForm] = useState(false);
  const [showAccessorio, setShowAccessorio] = useState(false);
  const [showPulizia, setShowPulizia] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/equipment");
      setItems(data);
    } catch {
      toast.error("Errore nel caricamento delle attrezzature");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/equipment/categories").then(({ data }) => setCats(data)).catch(() => {});
    load();
  }, []);

  const onSubmit = async (form) => {
    try {
      if (editing) await api.put(`/equipment/${editing.id}`, form);
      else await api.post("/equipment", form);
      toast.success("Salvato");
      setShowForm(false); setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const q = search.trim().toLowerCase();
  const searching = q.length > 0;
  const match = (e) => [e.identifier, e.brand, e.model, e.tipo, e.owner, e.utilizzatore]
    .some(v => (v || "").toLowerCase().includes(q));

  const allCats = Array.from(new Set([...(cats || []), ...items.map(e => e.category)]))
    .filter(Boolean).sort();

  const T = today(), D30 = due30();
  const isOverdue = (e) => e.next_maintenance_date && e.next_maintenance_date < T;

  const statsFor = (c) => {
    const list = items.filter(e => e.category === c);
    return { total: list.length, overdue: list.filter(isOverdue).length };
  };

  const visible = searching
    ? items.filter(match)
    : (openCat ? items.filter(e => e.category === openCat) : []);

  /** Scarica un file dal server tenendo il nome che gli dà lui. */
  const scarica = async (percorso, nomeDiRiserva, messaggio) => {
    try {
      const r = await authFetch(percorso);
      if (!r.ok) throw new Error();
      const cd = r.headers.get("content-disposition") || "";
      const nome = /filename="?([^";]+)"?/.exec(cd)?.[1] || nomeDiRiserva;
      const u = window.URL.createObjectURL(await r.blob());
      const a = document.createElement("a");
      a.href = u; a.download = nome; a.click();
      window.URL.revokeObjectURL(u);
      toast.success(messaggio);
    } catch { toast.error("Errore nel download"); }
  };

  // I libri di manutenzione di tutto il parco, uno per macchina, dentro un
  // archivio: è quello che si consegna quando un ente chiede lo storico.
  const scaricaLibri = () => {
    const p = new URLSearchParams();
    if (!searching && openCat) p.set("category", openCat);
    const qs = p.toString();
    toast.info("Preparo i libri, può volerci qualche secondo...");
    return scarica(`/equipment/libri-manutenzione/zip${qs ? `?${qs}` : ""}`,
                   "libri_manutenzione_SGM.zip", "Libri di manutenzione scaricati");
  };

  // Tutti gli interventi su un foglio, da filtrare: è dove si fanno i conti.
  const scaricaManutenzioni = () => {
    const p = new URLSearchParams();
    if (!searching && openCat) p.set("category", openCat);
    const qs = p.toString();
    return scarica(`/equipment/export-manutenzioni${qs ? `?${qs}` : ""}`,
                   "manutenzioni_SGM.xlsx", "Foglio delle manutenzioni scaricato");
  };

  // Fuori da una categoria e senza ricerca si sta guardando tutto il parco:
  // gli export prendono tutto, e i pulsanti lo dicono.
  const tuttoIlParco = !openCat && !searching;

  // Scarica in Excel l'elenco della categoria aperta (o dei risultati di ricerca)
  const esportaElenco = async () => {
    try {
      const p = new URLSearchParams();
      if (searching) p.set("q", search.trim());
      else if (openCat) p.set("category", openCat);
      const qs = p.toString();
      const r = await authFetch(`/equipment/export-elenco${qs ? `?${qs}` : ""}`);
      if (!r.ok) throw new Error();
      const cd = r.headers.get("content-disposition") || "";
      const nome = /filename="?([^";]+)"?/.exec(cd)?.[1] || "elenco_attrezzature_SGM.xlsx";
      const u = window.URL.createObjectURL(await r.blob());
      const a = document.createElement("a");
      a.href = u; a.download = nome; a.click();
      window.URL.revokeObjectURL(u);
      toast.success("Elenco scaricato");
    } catch { toast.error("Errore nel download dell'elenco"); }
  };

  return (
    <div className="space-y-6" data-testid="attrezzature-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 mb-2">Mezzi e attrezzature</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Elenco</h1>
          <p className="text-sm text-slate-500 mt-1">
            {items.length} mezzi/strumenti in {allCats.length} categorie
          </p>
        </div>
        {puoAggiungere && (
          <div className="flex gap-2 flex-wrap">
            {gestisce && (<>
            <RouterLink to="/archivio-attrezzature">
              <Button variant="outline" className="h-12" data-testid="archivio-eq-btn">
                <Archive className="w-4 h-4 mr-2" /> Archivio dismesse
              </Button>
            </RouterLink>
            <ImportLibriManutenzione onDone={load} />
            <SpostaCategoria categorie={allCats} onDone={load} />
            <DoppioniInterventi onDone={load} />
            <BulkImportBtn onDone={load} />
            {isAdmin && (
              <Button variant="outline" className="h-12 text-red-600" onClick={() => setShowPulizia(true)}
                      data-testid="pulizia-mezzi-btn">
                <Trash2 className="w-4 h-4 mr-2" /> Pulizia documenti
              </Button>
            )}
            </>)}
            <Button variant="outline" className="h-12" onClick={() => setShowAccessorio(true)}
                    data-testid="add-accessorio-btn">
              <Plus className="w-4 h-4 mr-2" /> Nuovo accessorio
            </Button>
            <Button className="bg-orange-500 hover:bg-orange-600 text-white h-12"
                    onClick={() => { setEditing(null); setShowForm(true); }} data-testid="add-eq-btn">
              <Plus className="w-4 h-4 mr-2" /> Nuova attrezzatura
            </Button>
          </div>
        )}
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input placeholder="Cerca in tutte le attrezzature (targa, marca, modello, tipo)..."
               className="pl-9 pr-9 h-12" value={search}
               onChange={(e) => setSearch(e.target.value)} data-testid="eq-search" />
        {searching && (
          <button type="button" onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Briciole e export. La riga c'e' anche sull'elenco delle categorie,
          dove non si sta guardando dentro niente: li' gli export prendono
          tutto il parco, mezzi e attrezzature insieme. Prima bisognava entrare
          in una categoria per trovarli, e chi li cercava non li vedeva. */}
      <div className="flex items-center justify-between gap-2 text-sm flex-wrap">
          <div className="flex items-center gap-2">
            {(openCat || searching) && (<>
            <button type="button" onClick={() => { setOpenCat(null); setSearch(""); }}
                    className="text-orange-600 hover:underline inline-flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Tutte le categorie
            </button>
            {!searching && openCat && (<>
              <ChevronRight className="w-4 h-4 text-slate-400" />
              <span className="font-semibold text-slate-700">{openCat}</span>
              <Badge variant="outline">{visible.length}</Badge>
            </>)}
            {searching && (<>
              <ChevronRight className="w-4 h-4 text-slate-400" />
              <span className="font-semibold text-slate-700">Risultati ricerca</span>
              <Badge variant="outline">{visible.length}</Badge>
            </>)}
            </>)}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={esportaElenco}
                    title={tuttoIlParco
                      ? "Scarica in Excel tutti i mezzi e le attrezzature, in ordine di categoria"
                      : "Scarica in Excel l'elenco che stai guardando"}
                    data-testid="export-attrezzature-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              {tuttoIlParco ? "Export di tutto l'elenco" : "Export elenco"}
            </Button>
            {vedeICosti(user) && (
              <Button variant="outline" size="sm" onClick={scaricaManutenzioni}
                      title="Tutti gli interventi su un foglio, con il conto per macchina"
                      data-testid="export-manutenzioni-btn">
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Export manutenzioni
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={scaricaLibri}
                    title="I libri di manutenzione, uno per macchina, in un archivio"
                    data-testid="export-libri-btn">
              <BookOpen className="w-4 h-4 mr-2" /> Libri di manutenzione
            </Button>
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={apriUniforma}
                      title="Dà a ogni macchina l'elenco di controlli previsto per la sua categoria"
                      data-testid="uniforma-controlli-btn">
                <ListChecks className="w-4 h-4 mr-2" /> Uniforma controlli
              </Button>
            )}
          </div>
      </div>

      {/* Anteprima prima di scrivere: quali macchine cambiano e cosa avevano.
          Sui dati veri non si preme un pulsante al buio. */}
      <Dialog open={!!uniforma} onOpenChange={(v) => !v && setUniforma(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Uniformare l'elenco dei controlli</DialogTitle>
          </DialogHeader>
          {uniforma && (
            <div className="space-y-3 text-sm">
              <p className="text-slate-600">
                Ogni macchina prende l'elenco previsto per la sua categoria.
                Attrezzature, strumenti di collaudo, macchine speciali,
                cercafughe ed esplosimetri restano come sono: hanno controlli
                loro.
              </p>
              <div className="flex flex-wrap gap-3">
                <span className="px-3 py-1 rounded-full bg-orange-50 text-orange-700 border border-orange-200">
                  {uniforma.quante_cambiate} da allineare
                </span>
                <span className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                  {uniforma.gia_a_posto} già a posto
                </span>
                <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                  {uniforma.saltate} lasciate stare
                </span>
              </div>
              {uniforma.quante_cambiate > 0 && (
                <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-80 overflow-y-auto">
                  {uniforma.cambiate.map((c) => (
                    <div key={c.id} className="p-3">
                      <div className="font-medium">
                        {c.identifier} <span className="text-xs text-slate-500">{c.categoria}</span>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-2 mt-1 text-xs">
                        <div>
                          <div className="text-slate-400 mb-0.5">adesso</div>
                          {c.prima.length === 0
                            ? <div className="text-slate-400">nessun controllo</div>
                            : c.prima.map((v, i) => <div key={i} className="text-slate-600">{v}</div>)}
                        </div>
                        <div>
                          <div className="text-slate-400 mb-0.5">diventa</div>
                          {c.dopo.map((v, i) => <div key={i} className="text-[#0067b2]">{v}</div>)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-slate-500">
                I controlli già registrati non si toccano: restano nel libro
                con le spunte del giorno in cui sono stati fatti.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setUniforma(null)}>Annulla</Button>
            <Button className="bg-orange-500 hover:bg-orange-600"
                    disabled={applicando || !uniforma?.quante_cambiate}
                    onClick={applicaUniforma} data-testid="conferma-uniforma">
              {applicando ? "Applico..." : "Applica a tutte"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {loading && <p className="text-sm text-slate-500 py-8 text-center">Caricamento...</p>}

      {/* ALBERO CATEGORIE */}
      {!loading && !searching && !openCat && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {allCats.length === 0 && (
            <Card className="col-span-full border-dashed border-2">
              <CardContent className="p-8 text-center text-slate-500">
                Nessuna attrezzatura registrata.
              </CardContent>
            </Card>
          )}
          {allCats.map(c => {
            const s = statsFor(c);
            return (
              <button key={c} type="button" onClick={() => setOpenCat(c)}
                      data-testid={`eq-cat-${c}`} className="text-left">
                <Card className="border-slate-200 hover:shadow-md hover:border-orange-300 transition-all h-full">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-11 h-11 rounded bg-orange-50 flex items-center justify-center flex-shrink-0">
                      <Folder className="w-5 h-5 text-orange-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold leading-tight truncate">{c}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {s.total} {s.total === 1 ? "mezzo" : "mezzi"}
                      </div>
                      {s.overdue > 0 && (
                        <Badge variant="destructive" className="mt-1 text-[10px]">
                          <AlertTriangle className="w-3 h-3 mr-1" />{s.overdue} scadut{s.overdue === 1 ? "a" : "e"}
                        </Badge>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
                  </CardContent>
                </Card>
              </button>
            );
          })}
        </div>
      )}

      {/* ELENCO SCHEDE */}
      {!loading && (searching || openCat) && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visible.length === 0 && (
            <Card className="col-span-full border-dashed border-2">
              <CardContent className="p-8 text-center text-slate-500">
                {searching ? "Nessun risultato per questa ricerca." : "Nessuna attrezzatura in questa categoria."}
              </CardContent>
            </Card>
          )}
          {visible.map(e => <EqCard key={e.id} e={e} T={T} D30={D30} />)}
        </div>
      )}

      {showForm && <EqForm initial={editing} cats={allCats} onSubmit={onSubmit}
                           onClose={() => { setShowForm(false); setEditing(null); }} />}
      {showAccessorio && <AccessorioForm onClose={() => setShowAccessorio(false)}
                                         onDone={() => { setShowAccessorio(false); load(); }} />}

      {showPulizia && (
        <PuliziaFascicoliDialog
          base="/equipment/documents"
          titolo="Pulizia documenti di mezzi e attrezzature"
          intestazioneProprietario="Mezzo"
          onClose={() => setShowPulizia(false)} onDone={load} />
      )}
    </div>
  );
}

const giornoIt = (iso) =>
  (iso || "").substring(0, 10).split("-").reverse().join("/");

function EqCard({ e, T, D30 }) {
  const nd = e.next_maintenance_date;
  const doc = e.documento_in_scadenza;
  const overdue = nd && nd < T;
  const dueSoon = nd && nd >= T && nd <= D30;
  return (
    <Link to={`/attrezzature/${e.id}`} data-testid={`eq-card-${e.id}`}>
      <Card className="border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer h-full">
        <CardContent className="p-5">
          <div className="flex items-start gap-3 mb-2">
            {e.photo_url
              ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api${e.photo_url}`} alt=""
                     className="w-16 h-16 rounded object-cover border border-slate-200" />
              : <div className="w-16 h-16 rounded bg-orange-50 flex items-center justify-center">
                  <EquipmentIcon category={e.category} className="w-7 h-7 text-orange-500" />
                </div>}
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">{e.category}</div>
              <div className="font-display font-bold text-lg leading-tight truncate">
                {[e.brand, e.model].filter(Boolean).join(" ") || e.identifier}
              </div>
              <div className="text-xs text-slate-500 truncate">{e.tipo}{e.tipo && e.year && " · "}{e.year}</div>
              <div className="text-xs font-mono text-slate-700 truncate mt-1">Matr/Targa: <strong>{e.identifier}</strong></div>
              {e.owner && <div className="text-[10px] text-slate-400 truncate">Proprietà: {e.owner}</div>}
              {e.utilizzatore && <div className="text-xs text-slate-700 truncate">Utilizzatore: <strong>{e.utilizzatore}</strong></div>}
            </div>
          </div>
          <div className="flex items-center justify-between mt-3">
            <Badge className={`${STATUS_COLOR[e.status] || ""} border-0`}>{STATUS_LABEL[e.status] || e.status}</Badge>
            {overdue && <Badge variant="destructive" className="text-[10px]"><AlertTriangle className="w-3 h-3 mr-1" />Scaduta</Badge>}
            {!overdue && !doc && e.revisione_mancante && (
              <Badge className="bg-slate-700 text-white border-0 text-[10px]">
                Revisione da inserire
              </Badge>
            )}
            {!overdue && doc?.stato === "scaduto" && (
              <Badge variant="destructive" className="text-[10px]">
                <AlertTriangle className="w-3 h-3 mr-1" />Documento scaduto
              </Badge>
            )}
            {dueSoon && <Badge className="bg-amber-500 text-white border-0 text-[10px]"><Wrench className="w-3 h-3 mr-1" />In scadenza</Badge>}
            {!overdue && !dueSoon && nd && <Badge variant="outline" className="text-[10px]"><CheckCircle2 className="w-3 h-3 mr-1" />OK</Badge>}
          </div>
          {nd && <div className="text-[10px] text-slate-500 mt-1">Prossima manutenzione: {nd.substring(0, 10)}</div>}
          {/* La scadenza scritta su un documento - revisione, assicurazione,
              taratura di uno strumento - avvisa come la manutenzione: prima
              restava a guardare dentro la scheda e scadeva in silenzio. */}
          {!doc && e.revisione_mancante && (
            <div className="text-[10px] text-slate-500 mt-1">
              Mezzo targato: manca la revisione in archivio
            </div>
          )}
          {doc && (
            <div className={`text-[10px] mt-1 ${
              doc.stato === "scaduto" ? "text-red-600 font-medium" : "text-amber-700"}`}>
              {doc.tipo}: {doc.titolo} ·{" "}
              {doc.stato === "scaduto"
                ? `scaduto il ${giornoIt(doc.scadenza)}`
                : `scade il ${giornoIt(doc.scadenza)}`}
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

/**
 * L'import in blocco dei documenti delle macchine.
 *
 * La finestra e' la stessa dei dipendenti: cambia il profilo, cioe' gli
 * indirizzi e le etichette. Cosi' la scorciatoia che fa compilare il modello
 * leggendo l'archivio vale anche qui, e quello che si corregge in una si
 * corregge in tutte.
 */
function BulkImportBtn({ onDone }) {
  const [aperta, setAperta] = useState(false);
  return (<>
    <Button variant="outline" className="h-12" onClick={() => setAperta(true)}
            data-testid="bulk-import-btn">
      <UploadCloud className="w-4 h-4 mr-2" /> Import massivo
    </Button>
    {aperta && (
      <ImportDocumentiDialog profilo={PROFILO_MEZZI}
                             onClose={() => setAperta(false)}
                             onDone={onDone} />
    )}
  </>);
}


// Un accessorio di sollevamento o anticaduta nasce già con la checklist del
// suo tipo e con il primo controllo fatto: il giorno in cui entra in azienda
// è il giorno in cui lo si guarda. Non si carica storico da fuori.
function AccessorioForm({ onClose, onDone }) {
  const oggi = new Date().toISOString().slice(0, 10);
  const [tipi, setTipi] = useState([]);
  const [persone, setPersone] = useState([]);
  const [f, setF] = useState({ tipo: "", identifier: "", brand: "", model: "", portata: "", notes: "",
                               cadenza_mesi: "", data_inserimento: oggi, eseguito_da_id: "", note_controllo: "" });
  const [anomalie, setAnomalie] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/equipment/accessori/tipi").then(({ data }) => {
      setTipi(data);
      if (data.length) setF((v) => ({ ...v, tipo: data[0].codice, cadenza_mesi: String(data[0].cadenza_mesi) }));
    }).catch(() => toast.error("Tipi di accessorio non disponibili"));
    api.get("/employees").then(({ data }) => setPersone(data || [])).catch(() => {});
  }, []);

  const tipo = tipi.find((x) => x.codice === f.tipo);
  const scegliTipo = (codice) => {
    const nuovo = tipi.find((x) => x.codice === codice);
    setF({ ...f, tipo: codice, cadenza_mesi: nuovo ? String(nuovo.cadenza_mesi) : f.cadenza_mesi });
    setAnomalie([]);
  };
  const toggla = (voce) => setAnomalie((a) => (a.includes(voce) ? a.filter((x) => x !== voce) : [...a, voce]));

  const salva = async () => {
    if (!f.identifier.trim()) { toast.error("Serve la matricola o il numero di riconoscimento"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/equipment/accessorio", {
        ...f, cadenza_mesi: Number(f.cadenza_mesi) || null, eseguito_da_id: f.eseguito_da_id || null, anomalie,
      });
      toast.success(`${data.attrezzatura.identifier} in elenco, primo controllo registrato`
        + (anomalie.length ? ` con ${anomalie.length} anomalie` : ""));
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Nuovo accessorio di sollevamento o anticaduta</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            Il pezzo entra in elenco con la checklist del suo tipo e con il primo controllo già
            registrato alla data di inserimento; da lì parte la cadenza. Ex SC IO 4.4.6.1.3.7-13.
          </p>
          <div>
            <Label>Tipo</Label>
            <select className="w-full h-10 border border-slate-200 rounded px-3 text-sm bg-white" value={f.tipo}
                    onChange={(e) => scegliTipo(e.target.value)} data-testid="accessorio-tipo">
              {tipi.map((x) => <option key={x.codice} value={x.codice}>{x.nome}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Matricola / n. di riconoscimento</Label>
              <Input value={f.identifier} onChange={(e) => setF({ ...f, identifier: e.target.value })}
                     placeholder="es. 2405468 0002" data-testid="accessorio-matricola" />
            </div>
            <div>
              <Label>Produttore</Label>
              <Input value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} placeholder="es. SME Lifting" />
            </div>
            <div>
              <Label>Modello / tipo costruttivo</Label>
              <Input value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} placeholder="es. doppia asola, 3 m" />
            </div>
            <div>
              <Label>Portata o caratteristiche</Label>
              <Input value={f.portata} onChange={(e) => setF({ ...f, portata: e.target.value })} placeholder="es. dritto 800 kg, braca a U 1000 kg" />
            </div>
            <div>
              <Label>Data di inserimento in azienda</Label>
              <Input type="date" value={f.data_inserimento} onChange={(e) => setF({ ...f, data_inserimento: e.target.value })} />
            </div>
            <div>
              <Label>Controllo ogni</Label>
              <select className="w-full h-10 border border-slate-200 rounded px-3 text-sm bg-white" value={f.cadenza_mesi}
                      onChange={(e) => setF({ ...f, cadenza_mesi: e.target.value })}>
                {[3, 4, 6, 12].map((m) => <option key={m} value={m}>{m} mesi</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label>Primo controllo eseguito da</Label>
            <select className="w-full h-10 border border-slate-200 rounded px-3 text-sm bg-white" value={f.eseguito_da_id}
                    onChange={(e) => setF({ ...f, eseguito_da_id: e.target.value })}>
              <option value="">— chi è collegato —</option>
              {persone.map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
            </select>
          </div>
          {tipo && (
            <div className="border border-slate-200 rounded p-3 space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                Esame visivo all&apos;inserimento — spunta solo quello che NON va
              </div>
              {tipo.voci.map((v) => (
                <label key={v} className={`flex items-center gap-2 text-sm p-1 rounded ${anomalie.includes(v) ? "bg-red-50 text-red-800" : ""}`}>
                  <input type="checkbox" checked={anomalie.includes(v)} onChange={() => toggla(v)} />
                  {v}
                </label>
              ))}
              <Input className="mt-2" value={f.note_controllo} placeholder="Note del controllo (facoltative)"
                     onChange={(e) => setF({ ...f, note_controllo: e.target.value })} />
            </div>
          )}
          <div>
            <Label>Note sulla scheda</Label>
            <Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={salva} disabled={busy || !tipi.length} className="bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="salva-accessorio">
            {busy ? "Salvo..." : "Crea e registra il controllo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EqForm({ initial, cats, onSubmit, onClose }) {
  const [f, setF] = useState(initial || {
    category: cats[0] || "AUTOCARRI", identifier: "", brand: "", model: "", tipo: "",
    year: "", status: "active", maintenance_interval_months: 12,
    next_maintenance_date: "", notes: "", owner: "", utilizzatore_id: null
  });
  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      ...f,
      year: f.year ? parseInt(f.year) : null,
      maintenance_interval_months: parseInt(f.maintenance_interval_months) || null
    });
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{initial ? "Modifica" : "Nuova"} attrezzatura</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Categoria</Label>
            <select className="w-full h-10 border border-slate-200 rounded px-3"
                    value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
              {cats.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div><Label>Identificativo (targa/matricola) *</Label>
            <Input value={f.identifier} onChange={(e) => setF({ ...f, identifier: e.target.value })} required /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Marca</Label><Input value={f.brand || ""} onChange={(e) => setF({ ...f, brand: e.target.value })} /></div>
            <div><Label>Modello</Label><Input value={f.model || ""} onChange={(e) => setF({ ...f, model: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Tipo</Label><Input value={f.tipo || ""} onChange={(e) => setF({ ...f, tipo: e.target.value })} /></div>
            <div><Label>Anno</Label><Input type="number" value={f.year || ""} onChange={(e) => setF({ ...f, year: e.target.value })} /></div>
          </div>
          <div><Label>Proprietà</Label>
            <Input value={f.owner || ""} placeholder="SGM IMPIANTI SRL"
                   onChange={(e) => setF({ ...f, owner: e.target.value })} />
            <p className="text-xs text-slate-500 mt-1">
              Si compila solo se la macchina è di qualcun altro — a noleggio o
              in custodia. Lasciandolo vuoto ci va SGM IMPIANTI SRL.
            </p>
          </div>
          <CampoUtilizzatore categoria={f.category} value={f.utilizzatore_id}
                             onChange={(v) => setF({ ...f, utilizzatore_id: v })} />
          <div><Label>Stato</Label>
            <select className="w-full h-10 border border-slate-200 rounded px-3"
                    value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
              <option value="active">Attiva</option>
              <option value="maintenance">In manutenzione</option>
              <option value="dismissed">Dismessa</option>
              <option value="stolen">Rubata</option>
              <option value="scrapped">Smaltita</option>
              <option value="sold">Venduta</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Cadenza manutenzione</Label>
              <select className="w-full h-10 border border-slate-200 rounded px-3"
                      value={f.maintenance_interval_months || ""}
                      onChange={(e) => setF({ ...f, maintenance_interval_months: e.target.value })}>
                {CADENCE_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div><Label>Prossima manutenzione</Label>
              <Input type="date" value={f.next_maintenance_date || ""}
                     onChange={(e) => setF({ ...f, next_maintenance_date: e.target.value })} /></div>
          </div>
          <div><Label>Note</Label><Input value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
            <Button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white">Salva</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
